import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class EcRefreshSetupService {

  constructor(private http: HttpClient) { }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
  }

  private buildFormData(data: any): string {
    const formData = new URLSearchParams();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return formData.toString();
  }

  save(payload: any, filterParams: any, rowData: any, baseUrl: string): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=upobod`;

    const jsonSettings = {
      tax_year: filterParams?.tax_year
    };

    const params = _.merge({}, filterParams);
    params.json_data = JSON.stringify(payload);
    params.scenarioCode = payload.SCENARIO_TYPE_CODE;
    params.jsonSettings = JSON.stringify(jsonSettings);
    params.is_issue_key_enabled = 'N';

    return this.http.post(url, this.buildFormData(params), { headers: this.getHeaders(), withCredentials: true })
      .pipe(map(response => response as any));
  }

  update(payload: any, filterParams: any, rowData: any, baseUrl: string): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=rghm10`;

    const params = _.merge({}, filterParams);
    params.json_data = JSON.stringify(payload);
    params.is_issue_key_enabled = 'N';

    return this.http.post(url, this.buildFormData(params), { headers: this.getHeaders(), withCredentials: true })
      .pipe(map(response => response as any));
  }

  delete(taxYear: string, groupObjKey: string, filterParams: any, rowData: any, baseUrl: string): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=yogrs0`;

    const params = _.merge({}, filterParams);
    params.tax_year = taxYear;
    params.group_obj_key = groupObjKey;
    params.is_issue_key_enabled = 'N';

    return this.http.post(url, this.buildFormData(params), { headers: this.getHeaders(), withCredentials: true })
      .pipe(map(response => response as any));
  }

  loadScenarioTypeCode(baseUrl: string, filterParams: any, rowData: any): Observable<any> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      tax_year: filterParams?.tax_year || rowData?.TAX_YEAR || new Date().getFullYear(),
      scenario_type_code: null,
      scenario_desc: null
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get(url).pipe(
      map((response: any) => {
        // Handle different response structures
        if (response && response.jsonObject) {
          return response.jsonObject;
        } else if (response && response.data) {
          return response.data;
        } else if (Array.isArray(response)) {
          return response;
        } else if (response && response.result) {
          return response.result;
        }
        return [];
      })
    );
  }

  loadScenarioDesc(selectedValue: string, baseUrl: string, filterParams: any, rowData: any): Observable<any> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      tax_year: filterParams?.tax_year || rowData?.TAX_YEAR || new Date().getFullYear(),
      scenario_type_code: selectedValue,
      scenario_desc: null
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get(url).pipe(
      map((response: any) => {
        // Handle different response structures
        if (response && response.jsonObject) {
          return response.jsonObject;
        } else if (response && response.data) {
          return response.data;
        } else if (Array.isArray(response)) {
          return response;
        } else if (response && response.result) {
          return response.result;
        }
        return [];
      })
    );
  }

  loadGroupObjKey(scenarioTypeCode: string, scenarioDesc: string, baseUrl: string, filterParams: any, rowData: any): Observable<any> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      tax_year: filterParams?.tax_year || rowData?.TAX_YEAR || new Date().getFullYear(),
      scenario_type_code: scenarioTypeCode,
      scenario_desc: scenarioDesc
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get(url).pipe(
      map((response: any) => {
        // Handle different response structures
        if (response && response.jsonObject) {
          return response.jsonObject;
        } else if (response && response.data) {
          return response.data;
        } else if (Array.isArray(response)) {
          return response;
        } else if (response && response.result) {
          return response.result;
        }
        return [];
      })
    );
  }

  convertDateFormats(jsonObj: any): any {
    if (jsonObj.BATCH_START_DATE) {
      jsonObj.BATCH_START_DATE = this.formatDate(jsonObj.BATCH_START_DATE);
    }
    if (jsonObj.BATCH_END_DATE && jsonObj.BATCH_END_DATE !== 'Invalid date' && jsonObj.BATCH_END_DATE !== '') {
      jsonObj.BATCH_END_DATE = this.formatDate(jsonObj.BATCH_END_DATE);
    } else {
      jsonObj.BATCH_END_DATE = null;
    }
    if (jsonObj.DEFAULT_TAX_YEAR_BEGIN) {
      jsonObj.DEFAULT_TAX_YEAR_BEGIN = this.formatDate(jsonObj.DEFAULT_TAX_YEAR_BEGIN);
    }
    if (jsonObj.DEFAULT_TAX_YEAR_END) {
      jsonObj.DEFAULT_TAX_YEAR_END = this.formatDate(jsonObj.DEFAULT_TAX_YEAR_END);
    }
    return jsonObj;
  }

  private formatDate(date: any): string {
    if (!date) return '';

    if (typeof date === 'string') {
      // ISO dates are parsed as UTC by Date, which shifts a day in negative offsets
      const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
      if (iso) {
        return `${iso[2]}/${iso[3]}/${iso[1]}`;
      }
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        return date;
      }
    }

    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }
}

